import { Fragment, useEffect } from 'react'
import { useAuthState, useQuery } from 'packages/core'
import { Switch, Route, Redirect } from 'react-router-dom'
import withRequiredData from './withRequiredData'
import { PageContextProvider } from 'pageContext'
import { setIsWorkflowRenderable } from 'packages/core/auth/actions'
import {
    RequestViewMyRequests,
    RequestViewMyTasks,
    RequestViewAllRequests,
} from 'pages/Requests'
import { useAllApplications } from 'hooks'
import {
    ItemViewMyRequests,
    ItemViewMyTasks,
    ItemViewAllRequests,
} from 'pages/RequestItems'

import {
    MyRequestsWorkflows,
    MyTasksWorkflows,
    AllWorkflows,
} from 'pages/CollaborationTasks'
import useSubcomponents from 'useSubcomponents'
import config from 'config'
import { getWorkflowWaitTimeInSeconds } from 'packages/core'
import { useAppState } from 'appContext'
import { IdleTimer } from 'packages/eid-ui'
import { useSessionTimeout } from 'hooks'
import {
    BusinessRequestItemsReports,
    BusinessRequestsReports,
    ItemApproverDecisionsReports,
} from '../Reports'
export const PageEnum = Object.freeze({
    TO_DO: 'ToDo',
    MY_REQUESTS: 'MyRequests',
    ALL: 'All',
    REPORTS: 'Reports',
})

export const TabEnum = Object.freeze({
    REQUEST_VIEW: 'RequestView',
    ITEM_VIEW: 'ItemView',
    COLLABORATION_TASKS: 'CollaborationTasks',
    ITEM_APPROVER_DECISIONS: 'ItemApproverDecisions',
})

const App = () => {
    const [{ isWorkflowIRenderable }, authDispatch] = useAuthState()
    const workflowWaitTime = getWorkflowWaitTimeInSeconds() * 1000
    const { data: loginSessionTimeout } = useSessionTimeout()
    const { data: allowedApps, isLoading } = useAllApplications()
    const [, dispatch] = useAppState()
    const query = useQuery()
    useEffect(() => {
        if (!isWorkflowIRenderable) {
            setTimeout(() => {
                authDispatch(setIsWorkflowRenderable(true))
            }, workflowWaitTime)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (config.ENABLE_DEV_FEATURES?.toLowerCase() === 'true') {
            dispatch({
                type: 'ENABLE_DEV_FEATURES',
            })
        }
        if (query.get('enableDevFeatures')) {
            query.delete('enableDevFeatures')
            dispatch({
                type: 'ENABLE_DEV_FEATURES',
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const {
        hasAccessToRequestViewMyRequestsPage,
        hasAccessToItemViewMyRequestsPage,
        hasAccessToWorkflowsMyRequestsPage,
        hasAccessToRequestViewMyTasksPage,
        hasAccessToItemViewMyTasksPage,
        hasAccessToWorkflowsMyTasksPage,
        hasAccessToRequestViewAllRequestsPage,
        hasAccessToItemViewAllRequestsPage,
        hasAccessToWorkflowsAllRequestsPage,
        hasAccessToItemApproverDecisionsReportsPage,
        hasAccessToBusinessRequestsReportsPage,
        hasAccessToBusinessRequestItemsReportsPage,
    } = useSubcomponents()

    const landingPage = config.LANDING_PAGE
    const landingTab = config.LANDING_TAB

    const allPages = [
        {
            pageKey: PageEnum.TO_DO,
            routePrefix: `${config.APP_SUBPATH}/myTasks`,
            tabs: [
                {
                    tabKey: TabEnum.REQUEST_VIEW,
                    routeSuffix: 'businessRequests',
                    hasAccess: () => hasAccessToRequestViewMyTasksPage,
                },
                {
                    tabKey: TabEnum.ITEM_VIEW,
                    routeSuffix: 'businessRequestItems',
                    hasAccess: () => hasAccessToItemViewMyTasksPage,
                },
                {
                    tabKey: TabEnum.COLLABORATION_TASKS,
                    routeSuffix: 'collaborationTasks',
                    hasAccess: () => hasAccessToWorkflowsMyTasksPage,
                },
            ],
        },
        {
            pageKey: PageEnum.MY_REQUESTS,
            routePrefix: `${config.APP_SUBPATH}/myRequests`,
            tabs: [
                {
                    tabKey: TabEnum.REQUEST_VIEW,
                    routeSuffix: 'businessRequests',
                    hasAccess: () => hasAccessToRequestViewMyRequestsPage,
                },
                {
                    tabKey: TabEnum.ITEM_VIEW,
                    routeSuffix: 'businessRequestItems',
                    hasAccess: () => hasAccessToItemViewMyRequestsPage,
                },
                {
                    tabKey: TabEnum.COLLABORATION_TASKS,
                    routeSuffix: 'collaborationTasks',
                    hasAccess: () => hasAccessToWorkflowsMyRequestsPage,
                },
            ],
        },
        {
            pageKey: PageEnum.ALL,
            routePrefix: `${config.APP_SUBPATH}/all`,
            tabs: [
                {
                    tabKey: TabEnum.REQUEST_VIEW,
                    routeSuffix: 'businessRequests',
                    hasAccess: () => hasAccessToRequestViewAllRequestsPage,
                },
                {
                    tabKey: TabEnum.ITEM_VIEW,
                    routeSuffix: 'businessRequestItems',
                    hasAccess: () => hasAccessToItemViewAllRequestsPage,
                },
                {
                    tabKey: TabEnum.COLLABORATION_TASKS,
                    routeSuffix: 'collaborationTasks',
                    hasAccess: () => hasAccessToWorkflowsAllRequestsPage,
                },
            ],
        },
        {
            pageKey: PageEnum.REPORTS,
            routePrefix: `${config.APP_SUBPATH}/reports`,
            tabs: [
                {
                    tabKey: TabEnum.ITEM_APPROVER_DECISIONS,
                    routeSuffix: 'itemApproverDecisions',
                    hasAccess: () =>
                        hasAccessToItemApproverDecisionsReportsPage,
                },
            ],
        },
    ]

    const getAccessiblePages = () => {
        return allPages
            .map((page) => {
                const accessibleTabs = page.tabs.filter((tab) =>
                    tab.hasAccess(),
                )
                if (!accessibleTabs.length) return null
                return { ...page, tabs: accessibleTabs }
            })
            .filter(Boolean)
    }

    const getLandingRoute = (landingPage, landingTab) => {
        const accessiblePages = getAccessiblePages()
        const buildRoute = (p, t) => `${p.routePrefix}/${t.routeSuffix}`

        if (landingPage && landingTab) {
            const foundPage = accessiblePages.find(
                (p) =>
                    p.pageKey.toLowerCase() ===
                    String(landingPage).toLowerCase(),
            )

            if (foundPage) {
                const foundTab = foundPage.tabs.find(
                    (t) =>
                        t.tabKey.toLowerCase() ===
                        String(landingTab).toLowerCase(),
                )

                if (foundTab) {
                    return buildRoute(foundPage, foundTab)
                } else {
                    return buildRoute(foundPage, foundPage.tabs[0])
                }
            } else {
                // If the page is invalid, see if we can match the landingTab in any page
                for (const p of accessiblePages) {
                    const matchingTab = p.tabs.find(
                        (t) =>
                            t.tabKey.toLowerCase() ===
                            String(landingTab).toLowerCase(),
                    )
                    if (matchingTab) {
                        // Return the first page that has this tab
                        return buildRoute(p, matchingTab)
                    }
                }
            }
        }

        if (landingPage && !landingTab) {
            const page = accessiblePages.find(
                (p) =>
                    p.pageKey.toLowerCase() ===
                    String(landingPage)?.toLowerCase(),
            )
            if (page && page.tabs.length) {
                return buildRoute(page, page.tabs[0])
            }
        }

        // If only tab is specified, return the first accessible page that has that tab
        if (!landingPage && landingTab) {
            for (const page of accessiblePages) {
                const tab = page.tabs.find(
                    (t) =>
                        t.tabKey.toLowerCase() ===
                        String(landingTab)?.toLowerCase(),
                )
                if (tab) return buildRoute(page, tab)
            }
        }

        // If no landing page or landing tab defined, just return the first accessible page and tab
        if (accessiblePages.length && accessiblePages[0].tabs.length) {
            return buildRoute(accessiblePages[0], accessiblePages[0].tabs[0])
        }

        // If no pages or tabs are accessible, return forbidden
        return '/forbidden'
    }

    const defaultRedirectTo = getLandingRoute(landingPage, landingTab)

    return (
        <Fragment>
            {!isWorkflowIRenderable && (
                <div style={{ display: 'none' }}>
                    <iframe
                        title={`EmpowerID`}
                        src={`${config.BASE_EID_URL}/UI/Common/SAML2/HiddenBlankPage`}
                    />
                </div>
            )}

            <Switch>
                <Redirect exact from={`/`} to={defaultRedirectTo} />
                <Redirect
                    exact
                    from={`${config.APP_SUBPATH}/`}
                    to={defaultRedirectTo}
                />

                {hasAccessToRequestViewMyRequestsPage && (
                    <Route
                        exact
                        path={`${config.APP_SUBPATH}/myRequests/businessRequests`}
                        render={(props) => (
                            <PageContextProvider key="RequestViewMyRequests">
                                <RequestViewMyRequests {...props} />
                            </PageContextProvider>
                        )}
                    />
                )}
                {hasAccessToRequestViewMyTasksPage && (
                    <Route
                        exact
                        path={`${config.APP_SUBPATH}/myTasks/businessRequests`}
                        render={(props) => (
                            <PageContextProvider key="RequestViewMyTasks">
                                <RequestViewMyTasks {...props} />
                            </PageContextProvider>
                        )}
                    />
                )}
                {hasAccessToRequestViewAllRequestsPage && (
                    <Route
                        exact
                        path={`${config.APP_SUBPATH}/all/businessRequests`}
                        render={(props) => (
                            <PageContextProvider key="RequestViewAllRequests">
                                <RequestViewAllRequests {...props} />
                            </PageContextProvider>
                        )}
                    />
                )}

                {hasAccessToItemViewMyRequestsPage && (
                    <Route
                        exact
                        path={`${config.APP_SUBPATH}/myRequests/businessRequestItems`}
                        render={(props) => (
                            <PageContextProvider key="ItemViewMyRequests">
                                <ItemViewMyRequests {...props} />
                            </PageContextProvider>
                        )}
                    />
                )}
                {hasAccessToItemViewMyTasksPage && (
                    <Route
                        exact
                        path={`${config.APP_SUBPATH}/myTasks/businessRequestItems`}
                        render={(props) => (
                            <PageContextProvider key="ItemViewMyTasks">
                                <ItemViewMyTasks {...props} />
                            </PageContextProvider>
                        )}
                    />
                )}
                {hasAccessToItemViewAllRequestsPage && (
                    <Route
                        exact
                        path={`${config.APP_SUBPATH}/all/businessRequestItems`}
                        render={(props) => (
                            <PageContextProvider key="ItemViewAllRequests">
                                <ItemViewAllRequests {...props} />
                            </PageContextProvider>
                        )}
                    />
                )}

                {hasAccessToWorkflowsMyRequestsPage && (
                    <Route
                        exact
                        path={`${config.APP_SUBPATH}/myRequests/collaborationTasks`}
                        render={(props) => (
                            <PageContextProvider key="CollaborationTasksMyRequests">
                                <MyRequestsWorkflows {...props} />
                            </PageContextProvider>
                        )}
                    />
                )}

                {hasAccessToWorkflowsMyTasksPage && (
                    <Route
                        exact
                        path={`${config.APP_SUBPATH}/myTasks/collaborationTasks`}
                        render={(props) => (
                            <PageContextProvider key="CollaborationTasksMyTasks">
                                <MyTasksWorkflows {...props} />
                            </PageContextProvider>
                        )}
                    />
                )}

                {hasAccessToWorkflowsAllRequestsPage && (
                    <Route
                        exact
                        path={`${config.APP_SUBPATH}/all/collaborationTasks`}
                        render={(props) => (
                            <PageContextProvider key="CollaborationTasksAll">
                                <AllWorkflows {...props} />
                            </PageContextProvider>
                        )}
                    />
                )}

                {hasAccessToItemApproverDecisionsReportsPage && (
                    <Route
                        exact
                        path={`${config.APP_SUBPATH}/reports/itemApproverDecisions`}
                        render={(props) => (
                            <PageContextProvider key="ItemApproverDecisionsReports">
                                <ItemApproverDecisionsReports {...props} />
                            </PageContextProvider>
                        )}
                    />
                )}
                {hasAccessToBusinessRequestsReportsPage && (
                    <Route
                        exact
                        path={`${config.APP_SUBPATH}/reports/businessRequests`}
                        render={(props) => (
                            <PageContextProvider key="BusinessRequestsReports">
                                <BusinessRequestsReports {...props} />
                            </PageContextProvider>
                        )}
                    />
                )}
                {hasAccessToBusinessRequestItemsReportsPage && (
                    <Route
                        exact
                        path={`${config.APP_SUBPATH}/reports/businessRequestItems`}
                        render={(props) => (
                            <PageContextProvider key="BusinessRequestItemsReports">
                                <BusinessRequestItemsReports {...props} />
                            </PageContextProvider>
                        )}
                    />
                )}

                <Redirect from="" to="/notFound" />
            </Switch>
            {loginSessionTimeout && !isLoading && (
                <IdleTimer
                    loginSessionTimeout={loginSessionTimeout}
                    allowedUrls={allowedApps}
                />
            )}
        </Fragment>
    )
}

export default withRequiredData(App)
